// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  selectProductsByTypeAndProcess,
  updateProducts,
  updateCartProduct,
  type ProductPayload,
  type MailboxType,
  type DecoratedProduct,
  getCart,
} from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';

import type { FetchResponse } from '@gandi/flow-types';

import { notifyDanger } from '@gandi/notifications';

import { useTranslator } from '@gandi/translate';

import { GET } from 'gandi.redux-fetcher';

import { addOrRemoveMailboxStandAloneProducts } from '../../../../../MailboxCreate/modules/actions';
import type { ThunkAction, GetState, Dispatch } from '../../../../../types';
import type { Mailbox } from '../types';

type MailboxesInfos = {
  data: Array<$Exact<Mailbox>>,
  shouldMigrate: boolean,
  totalCount: number,
};

const fetchDomainBoxes: (
  fqdn: string,
) => ThunkAction<Promise<MailboxesInfos>> = fqdn => async dispatch => {
  const response = await dispatch(
    GET(`/mail/boxes/${fqdn}/boxes`, {
      // $FlowFixMe[prop-missing]
      params: {
        page: 1,
        per_page: 10,
      },
    }),
  );

  const shouldMigrate = !!parseInt(
    response.headers.get('X-Must-Migrate') || 0,
    10,
  );

  return {
    shouldMigrate,
    data: response.data,
    totalCount: parseInt(response.headers.get('X-Total-Count') || 0, 10),
  };
};

/**
 * Will, at fqdn change (most probably component mount) fetch domain boxes and return an updated list
 */
export function useDomainRenewableBoxes(fqdn: string): {
  mailboxes: MailboxesInfos['data'],
  totalCount: MailboxesInfos['totalCount'],
} | void {
  const dispatch = useDispatch();
  const [infos, setInfos] = React.useState<{
    mailboxes: MailboxesInfos['data'],
    totalCount: MailboxesInfos['totalCount'],
  } | void>();
  const fetcher = React.useCallback(async () => {
    try {
      const { data, shouldMigrate, totalCount } = await dispatch(
        fetchDomainBoxes(fqdn),
      );
      if (shouldMigrate) {
        setInfos({ mailboxes: data, totalCount });
      }
    } catch (err) {
      // fails silently
    }
  }, [dispatch, fqdn]);

  React.useEffect(() => {
    // eslint-disable-next-line no-void
    void fetcher();
  }, [fetcher]);

  return infos;
}

export const updateMailboxCreatePayloadByFqdn =
  ({
    fqdn,
    mailbox_type,
    payload,
  }: {
    fqdn: Fqdn,
    mailbox_type: MailboxType,
    payload: ProductPayload,
  }): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch, getState) => {
    const productsData = selectProductsByTypeAndProcess(getState(), {
      process: 'create',
      productType: 'mailboxv2',
    })
      .filter(
        p => p.payload.fqdn === fqdn && p.payload.mailbox_type === mailbox_type,
      )
      .map(p => ({ ...p, payload: { ...p.payload, ...payload } }));

    if (productsData.length === 0) return Promise.resolve();

    // $FlowFixMe[prop-missing] ProductData from @gandi/cart is not accurate
    // $FlowFixMe[incompatible-exact]
    // $FlowFixMe[incompatible-call]
    return dispatch(updateProducts({ productsData }));
  };

const updateMailboxStandAloneCreatePayload =
  ({
    mailbox_type,
    duration_month,
    payload,
  }: {
    mailbox_type: MailboxType,
    duration_month: number,
    payload: ProductPayload,
  }): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch, getState) => {
    const productsData = selectProductsByTypeAndProcess(getState(), {
      process: 'create',
      productType: 'mailbox-standalone',
    })
      .filter(
        p =>
          p.payload.config_name === mailbox_type &&
          p.payload.duration_month === duration_month,
      )
      .map(p => ({ ...p, payload: { ...p.payload, ...payload } }));

    if (productsData.length === 0) return Promise.resolve();

    return dispatch(
      updateCartProduct({
        productId: productsData[0].id,
        payload: productsData[0].payload,
      }),
    );
  };

export function useOnCounterChange(
  product: DecoratedProduct,
  setLoading: Dispatch,
  loading: boolean,
): (quantity: number) => void {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  return (quantity: number) => {
    if (loading) {
      return;
    }
    setLoading(true);

    dispatch(
      addOrRemoveMailboxStandAloneProducts({
        quantity: Number(quantity),
        id: product.id,
      }),
    )
      .catch(() => {
        dispatch(
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          }),
        );
      })
      .then(() => {
        return dispatch(getCart());
      })
      .finally(() => {
        setLoading(false);
      });
  };
}

export function useOnAutoRenewChange(
  product: DecoratedProduct,
): (evt: SyntheticInputEvent<>) => Promise<FetchResponse<any, any> | void> {
  const dispatch = useDispatch();
  return (evt: SyntheticInputEvent<>) => {
    if (!product.payload.config_name) return Promise.resolve();

    return ((dispatch(
      updateMailboxStandAloneCreatePayload({
        // $FlowFixMe[incompatible-call] config_name is always defined
        mailbox_type: product.payload.config_name,
        // $FlowFixMe[incompatible-call] duration_month is always defined
        duration_month: product.payload.duration_month,
        payload: {
          autorenew: evt.target.checked,
        },
      }),
    ).then(() => dispatch(getCart())): any): Promise<FetchResponse<any, any>>);
  };
}
